/**
 * Shared utilities for custom template renderers.
 */

/**
 * Apply theme overrides as CSS custom properties.
 */
export function applyThemeOverrides(element: HTMLElement, overrides?: Record<string, string>): void {
  if (!overrides) return;

  for (const [key, value] of Object.entries(overrides)) {
    // Convert camelCase to kebab-case CSS property
    const cssProperty = `--pms-custom-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    element.style.setProperty(cssProperty, value);
  }
}

/**
 * Extract items array from various data formats.
 */
export function extractItems(data: Record<string, unknown>): unknown[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.cards)) {
    return data.cards;
  }
  if (Array.isArray(data.items)) {
    return data.items;
  }
  if (Array.isArray(data.data)) {
    return data.data;
  }
  // Single item
  return [data];
}

/**
 * Get nested value from object using dot notation.
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (typeof obj !== 'object' || obj === null) return undefined;

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Format field key as human-readable label.
 */
export function formatFieldLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

/**
 * Safely evaluate a formula string with given variables.
 * Only allows arithmetic operations - no function calls or property access.
 */
export function evaluateFormula(formula: string, variables: Record<string, number | string>): number {
  // Replace variable names with their values
  let expression = formula;

  // Sort by length descending to avoid partial replacements
  const sortedKeys = Object.keys(variables).sort((a, b) => b.length - a.length);

  for (const key of sortedKeys) {
    const value = variables[key];
    const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    // Use word boundary to avoid partial replacements
    expression = expression.replace(new RegExp(`\\b${key}\\b`, 'g'), String(numValue));
  }

  // Validate expression only contains safe characters
  if (!/^[\d\s+\-*/().%<>=!&|?:,]+$/.test(expression)) {
    throw new Error('Invalid formula expression');
  }

  // Use Function constructor for safe evaluation (no access to scope)
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  const fn = new Function(`return (${expression})`);
  const result = fn();

  if (typeof result !== 'number' || !isFinite(result)) {
    return 0;
  }

  return result;
}
